import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  FiCalendar, FiSearch, FiRefreshCw,
  FiClock, FiEdit2, FiTrash2, FiRotateCcw, FiBell,
  FiCheck, FiX, FiDatabase, FiFileText,
  FiDownload, FiPrinter, FiExternalLink, FiChevronRight, FiChevronDown,
  FiFilter, FiSliders
} from 'react-icons/fi';
import {
  getMonthlyLeaveSummary,
  superadminListLeaves,
  superadminUpdateLeave,
  superadminDeleteLeave,
  superadminGetBalances,
  superadminUpsertBalance,
  approveLeave,
  reverseLeaveRejection,
  remindLeaveManager,
  getEmployeeMonthlyBalance
} from '../../finance/api/superadminLeave';
import { listUsers } from '../../finance/api/admin';
import { fmtDate, fmtDateTime } from '../../utils/istDate';
import ConfirmModal from '../shared/ConfirmModal';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const LEAVE_TYPES = ['ALL', 'ANNUAL', 'SICK', 'CASUAL', 'EMERGENCY', 'MATERNITY', 'PATERNITY', 'UNPAID'];
const STATUS_OPTIONS = ['ALL', 'PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];

export default function HrLeaveManagementPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isAdminMode = location.pathname.startsWith('/admin');

  // Tab: 'summary' (Monthly Summary) | 'records' (Leave Records) | 'balances' (Balances Directory)
  const [activeTab, setActiveTab] = useState('summary');
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // Month & Year Filter
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [deptFilter, setDeptFilter] = useState('ALL');
  const [summaryStatusFilter, setSummaryStatusFilter] = useState('ALL');
  const [recordStatusFilter, setRecordStatusFilter] = useState('ALL');
  const [recordTypeFilter, setRecordTypeFilter] = useState('ALL');

  // Data
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [allLeaves, setAllLeaves] = useState([]);
  const [loadingLeaves, setLoadingLeaves] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Balances Tab
  const [selectedBalEmpId, setSelectedBalEmpId] = useState('');
  const [empBalances, setEmpBalances] = useState([]);
  const [empMonthlyBal, setEmpMonthlyBal] = useState(null);
  const [loadingBalDetails, setLoadingBalDetails] = useState(false);

  // Modals
  const [editLeaveModal, setEditLeaveModal] = useState({
    open: false,
    leave: null,
    formData: {
      status: 'PENDING',
      leaveType: 'ANNUAL',
      startDate: '',
      endDate: '',
      daysCount: 1,
      paidDays: 1,
      unpaidDays: 0,
      reason: '',
      remarks: '',
      penaltyApplied: false,
      isHalfDay: false,
      halfDaySession: 'FIRST_HALF',
    },
    saving: false
  });

  const [reverseModal, setReverseModal] = useState({
    open: false,
    leave: null,
    remarks: '',
    saving: false
  });

  const [balanceModal, setBalanceModal] = useState({
    open: false,
    employeeId: '',
    employeeName: '',
    year: now.getFullYear(),
    allocated: 24,
    used: 0,
    reserved: 0,
    carriedForward: 0,
    earnedLeave: 0,
    saving: false
  });

  const [actionModal, setActionModal] = useState({
    open: false,
    leave: null,
    status: 'APPROVED',
    remarks: '',
    saving: false
  });

  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    leave: null,
    saving: false
  });

  const [remindingId, setRemindingId] = useState(null);
  const [printDocOpen, setPrintDocOpen] = useState(false);
  const [printSearch, setPrintSearch] = useState('');
  const [printLopOnly, setPrintLopOnly] = useState(false);

  // Close audit modal on Escape
  useEffect(() => {
    if (!printDocOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') setPrintDocOpen(false);
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [printDocOpen]);

  // ─────────────────────────────────────────────────────────────
  // Fetch Functions
  // ─────────────────────────────────────────────────────────────

  const fetchMonthlySummary = useCallback(async (m = selectedMonth, y = selectedYear, silent = false) => {
    setLoadingSummary(true);
    try {
      const data = await getMonthlyLeaveSummary(m, y);
      setMonthlyReport(data);
      if (!silent) toast.success(`Leave summary generated for ${MONTH_NAMES[m - 1]} ${y}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate monthly leave summary');
    } finally {
      setLoadingSummary(false);
    }
  }, [selectedMonth, selectedYear]);

  const fetchAllLeaves = useCallback(async () => {
    setLoadingLeaves(true);
    try {
      const data = await superadminListLeaves();
      setAllLeaves(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load leave records');
    } finally {
      setLoadingLeaves(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await listUsers();
      const list = Array.isArray(res) ? res : [];
      setUsersList(list);
      if (!selectedBalEmpId && list.length > 0) {
        const first = list.find(u => u.employeeId) || list[0];
        if (first?.employeeId) setSelectedBalEmpId(first.employeeId);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingUsers(false);
    }
  }, [selectedBalEmpId]);

  const fetchEmpBalances = useCallback(async (empId, yr) => {
    if (!empId) return;
    setLoadingBalDetails(true);
    try {
      const [list, monthly] = await Promise.all([
        superadminGetBalances(empId).catch(() => []),
        getEmployeeMonthlyBalance(empId, yr).catch(() => null)
      ]);
      setEmpBalances(Array.isArray(list) ? list : []);
      setEmpMonthlyBal(monthly);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalDetails(false);
    }
  }, []);

  useEffect(() => {
    fetchMonthlySummary(selectedMonth, selectedYear, true);
    fetchAllLeaves();
    fetchUsers();
  }, [fetchMonthlySummary, fetchAllLeaves, fetchUsers, selectedMonth, selectedYear]);

  useEffect(() => {
    if (activeTab === 'balances' && selectedBalEmpId) {
      fetchEmpBalances(selectedBalEmpId, selectedYear);
    }
  }, [activeTab, selectedBalEmpId, selectedYear, fetchEmpBalances]);

  const handleMonthYearChange = (m, y) => {
    setSelectedMonth(m);
    setSelectedYear(y);
    fetchMonthlySummary(m, y, false);
  };

  // ─────────────────────────────────────────────────────────────
  // Filtering
  // ─────────────────────────────────────────────────────────────

  const filteredPrintEmployees = useMemo(() => {
    if (!monthlyReport?.employees) return [];
    return monthlyReport.employees.filter(emp => {
      if (printLopOnly && (!emp.lopDays || emp.lopDays <= 0)) return false;
      const q = printSearch.toLowerCase().trim();
      if (!q) return true;
      return (
        (emp.fullName && emp.fullName.toLowerCase().includes(q)) ||
        (emp.employeeId && emp.employeeId.toLowerCase().includes(q)) ||
        (emp.department && emp.department.toLowerCase().includes(q)) ||
        (emp.jobTitle && emp.jobTitle.toLowerCase().includes(q))
      );
    });
  }, [monthlyReport, printSearch, printLopOnly]);

  const availableDepts = useMemo(() => {
    const s = new Set();
    if (monthlyReport?.employees) {
      monthlyReport.employees.forEach(e => { if (e.department) s.add(e.department); });
    }
    usersList.forEach(u => { if (u.department) s.add(u.department); });
    return ['ALL', ...Array.from(s).sort()];
  }, [monthlyReport, usersList]);

  const filteredSummaryEmployees = useMemo(() => {
    if (!monthlyReport?.employees) return [];
    return monthlyReport.employees.filter(emp => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q ||
        (emp.fullName && emp.fullName.toLowerCase().includes(q)) ||
        (emp.employeeId && emp.employeeId.toLowerCase().includes(q)) ||
        (emp.jobTitle && emp.jobTitle.toLowerCase().includes(q)) ||
        (emp.department && emp.department.toLowerCase().includes(q));

      const matchesDept = deptFilter === 'ALL' ||
        (emp.department && emp.department.toUpperCase() === deptFilter.toUpperCase());

      let matchesStatus = true;
      if (summaryStatusFilter === 'HAS_LOP') matchesStatus = emp.lopDays > 0;
      else if (summaryStatusFilter === 'ON_LEAVE') matchesStatus = emp.totalLeavesTaken > 0;
      else if (summaryStatusFilter === 'ZERO_LEAVE') matchesStatus = emp.totalLeavesTaken === 0 && emp.lopDays === 0;
      else if (summaryStatusFilter === 'HAS_PENDING') matchesStatus = emp.pendingRequestsCount > 0;

      return matchesSearch && matchesDept && matchesStatus;
    });
  }, [monthlyReport, searchQuery, deptFilter, summaryStatusFilter]);

  const filteredLeaves = useMemo(() => {
    return allLeaves.filter(lv => {
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q ||
        (lv.employeeName && lv.employeeName.toLowerCase().includes(q)) ||
        (lv.employeeId && lv.employeeId.toLowerCase().includes(q)) ||
        (lv.reason && lv.reason.toLowerCase().includes(q)) ||
        (lv.leaveType && lv.leaveType.toLowerCase().includes(q));

      const matchesStatus = recordStatusFilter === 'ALL' || lv.status === recordStatusFilter;
      const matchesType = recordTypeFilter === 'ALL' || lv.leaveType === recordTypeFilter;

      return matchesSearch && matchesStatus && matchesType;
    });
  }, [allLeaves, searchQuery, recordStatusFilter, recordTypeFilter]);

  // ─────────────────────────────────────────────────────────────
  // Action Handlers
  // ─────────────────────────────────────────────────────────────

  const handleOpenEditLeave = (lv) => {
    setEditLeaveModal({
      open: true,
      leave: lv,
      formData: {
        status: lv.status || 'PENDING',
        leaveType: lv.leaveType || 'ANNUAL',
        startDate: lv.startDate ? String(lv.startDate).slice(0, 10) : '',
        endDate: lv.endDate ? String(lv.endDate).slice(0, 10) : '',
        daysCount: lv.daysCount ?? (lv.isHalfDay ? 0.5 : 1),
        paidDays: lv.paidDays ?? 0,
        unpaidDays: lv.unpaidDays ?? 0,
        reason: lv.reason || '',
        remarks: lv.remarks || '',
        penaltyApplied: !!lv.penaltyApplied,
        isHalfDay: !!lv.isHalfDay,
        halfDaySession: lv.halfDaySession || 'FIRST_HALF',
      },
      saving: false
    });
  };

  const handleSaveEditLeave = async () => {
    const { leave, formData } = editLeaveModal;
    if (!leave) return;
    setEditLeaveModal(p => ({ ...p, saving: true }));
    try {
      await superadminUpdateLeave(leave.id, formData);
      toast.success('Leave record corrected successfully');
      setEditLeaveModal(p => ({ ...p, open: false, saving: false }));
      fetchAllLeaves();
      fetchMonthlySummary(selectedMonth, selectedYear, true);
    } catch {
      toast.error('Failed to correct leave record');
      setEditLeaveModal(p => ({ ...p, saving: false }));
    }
  };

  const handleOpenReverse = (lv) => {
    setReverseModal({
      open: true,
      leave: lv,
      remarks: 'Reversed rejection & cleared LOP penalty by HR',
      saving: false
    });
  };

  const handleConfirmReverse = async () => {
    const { leave, remarks } = reverseModal;
    if (!leave) return;
    setReverseModal(p => ({ ...p, saving: true }));
    try {
      await reverseLeaveRejection(leave.id, remarks);
      toast.success('Rejection reversed. LOP penalty cleared and status set to PENDING.');
      setReverseModal({ open: false, leave: null, remarks: '', saving: false });
      fetchAllLeaves();
      fetchMonthlySummary(selectedMonth, selectedYear, true);
    } catch {
      toast.error('Failed to reverse rejection');
      setReverseModal(p => ({ ...p, saving: false }));
    }
  };

  const handleOpenApproval = (lv, status) => {
    setActionModal({
      open: true,
      leave: lv,
      status,
      remarks: status === 'APPROVED' ? 'Approved by HR' : 'Rejected by HR',
      saving: false
    });
  };

  const handleConfirmApproval = async () => {
    const { leave, status, remarks } = actionModal;
    if (!leave) return;
    setActionModal(p => ({ ...p, saving: true }));
    try {
      await approveLeave(leave.id, status, remarks);
      toast.success(`Leave request ${status.toLowerCase()} successfully`);
      setActionModal({ open: false, leave: null, status: 'APPROVED', remarks: '', saving: false });
      fetchAllLeaves();
      fetchMonthlySummary(selectedMonth, selectedYear, true);
    } catch {
      toast.error(`Failed to ${status.toLowerCase()} leave request`);
      setActionModal(p => ({ ...p, saving: false }));
    }
  };

  const handleRemindManager = async (lv) => {
    setRemindingId(lv.id);
    try {
      await remindLeaveManager(lv.id);
      toast.success('Reminder notification sent to manager');
    } catch {
      toast.error('Could not send manager reminder');
    } finally {
      setRemindingId(null);
    }
  };

  const handleOpenBalanceEdit = (empId, empName, rec = null) => {
    setBalanceModal({
      open: true,
      employeeId: empId,
      employeeName: empName || `Employee ${empId}`,
      year: rec?.year || selectedYear,
      allocated: rec?.allocated ?? 24,
      used: rec?.used ?? 0,
      reserved: rec?.reserved ?? 0,
      carriedForward: rec?.carriedForward ?? 0,
      earnedLeave: rec?.earnedLeave ?? 0,
      saving: false
    });
  };

  const handleSaveBalance = async () => {
    setBalanceModal(p => ({ ...p, saving: true }));
    try {
      await superadminUpsertBalance({
        employeeId: balanceModal.employeeId,
        year: parseInt(balanceModal.year, 10),
        allocated: parseFloat(balanceModal.allocated) || 0,
        used: parseFloat(balanceModal.used) || 0,
        reserved: parseFloat(balanceModal.reserved) || 0,
        carriedForward: parseFloat(balanceModal.carriedForward) || 0,
        earnedLeave: parseFloat(balanceModal.earnedLeave) || 0,
      });
      toast.success(`Leave balance saved for ${balanceModal.employeeId}`);
      setBalanceModal(p => ({ ...p, open: false, saving: false }));
      if (selectedBalEmpId === balanceModal.employeeId) {
        fetchEmpBalances(balanceModal.employeeId, balanceModal.year);
      }
      fetchMonthlySummary(selectedMonth, selectedYear, true);
    } catch {
      toast.error('Failed to update leave balance');
      setBalanceModal(p => ({ ...p, saving: false }));
    }
  };

  const handleDeleteLeave = async () => {
    const { leave } = deleteConfirm;
    if (!leave) return;
    setDeleteConfirm(p => ({ ...p, saving: true }));
    try {
      await superadminDeleteLeave(leave.id);
      toast.success('Leave record deleted');
      setDeleteConfirm({ open: false, leave: null, saving: false });
      fetchAllLeaves();
      fetchMonthlySummary(selectedMonth, selectedYear, true);
    } catch {
      toast.error('Failed to delete leave');
      setDeleteConfirm(p => ({ ...p, saving: false }));
    }
  };

  const handleViewEmployeeProfile = (emp) => {
    navigate(`/manager/employee-leave/${emp.employeeId}`, {
      state: {
        from: isAdminMode ? 'admin' : 'hr',
        emp: {
          fullName: emp.fullName,
          jobTitle: emp.jobTitle,
          department: emp.department
        }
      }
    });
  };

  const handleExportCsv = () => {
    if (!monthlyReport?.employees || monthlyReport.employees.length === 0) {
      toast.error('No employee data available to export');
      return;
    }

    const headers = [
      'Employee ID',
      'Employee Name',
      'Team / Department',
      'Job Title',
      'Attendance ID',
      'Month',
      'Year',
      'Leaves Taken (Days)',
      'Paid Days',
      'Unpaid Days',
      'LOP Days',
      'Salary Deduction % (LOP/30*100)',
      'Est. LOP Deduction (INR)',
      'Salary Setup Status',
      'Available Balance (Days)',
      'Allocated Annual Balance (Days)',
      'Pending Requests'
    ];

    const rows = monthlyReport.employees.map(emp => {
      const lop = emp.lopDays || 0;
      const salaryDeductionPct = ((lop / 30) * 100).toFixed(2) + '%';
      return [
        `"${emp.employeeId || ''}"`,
        `"${(emp.fullName || '').replace(/"/g, '""')}"`,
        `"${emp.department || ''}"`,
        `"${(emp.jobTitle || '').replace(/"/g, '""')}"`,
        `"${emp.attendanceId || ''}"`,
        MONTH_NAMES[selectedMonth - 1],
        selectedYear,
        emp.totalLeavesTaken ?? 0,
        emp.paidDays ?? 0,
        emp.unpaidDays ?? 0,
        lop,
        `"${salaryDeductionPct}"`,
        emp.lopDeductionAmount ?? 0,
        emp.hasSalaryConfigured ? 'Configured' : 'Pending Salary Setup',
        emp.currentAvailableBalance ?? 0,
        emp.annualAllocatedBalance ?? 0,
        emp.pendingRequestsCount ?? 0
      ];
    });

    const csvContent = 'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map(e => e.join(','))].join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Orakris_Leave_Summary_${MONTH_NAMES[selectedMonth - 1]}_${selectedYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('CSV export downloaded');
  };

  
  const generateAuditDocHtml = ({ monthlyReport: rpt, employees: emps, month, year, monthName }) => {
    const totalStaff = rpt?.totalEmployees ?? emps.length;
    const staffOnLeave = rpt?.employeesOnLeave ?? emps.filter(e => (e.totalLeavesTaken || 0) > 0).length;
    const totalLeaves = rpt?.totalLeaveDaysTaken ?? emps.reduce((acc, e) => acc + (e.totalLeavesTaken || 0), 0);
    const totalLop = rpt?.totalLopDays ?? emps.reduce((acc, e) => acc + (e.lopDays || 0), 0);
    const workingDays = rpt?.workingDays ?? 22;
    const calendarDays = rpt?.calendarDays ?? 30;
    const genDateStr = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const genTimeStr = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

    const rowsHtml = emps.map((e, idx) => {
      const lop = e.lopDays || 0;
      const leavesTaken = e.totalLeavesTaken ?? 0;
      const paid = e.paidDays ?? 0;
      const deductPct = lop > 0 ? ((lop / 30) * 100).toFixed(1) + '%' : '<span class="zero-muted">0.0%</span>';
      const lopBadge = lop > 0 ? `<span class="lop-tag">${lop} d</span>` : '<span class="zero-muted">0.0 d</span>';
      const estDeduct = lop > 0 && e.lopDeductionAmount > 0
        ? '₹' + Number(e.lopDeductionAmount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : (lop > 0 ? '<span class="pending-tag">Pending Setup</span>' : '<span class="zero-muted">—</span>');
      const dept = e.department && e.department !== '—' ? e.department : 'General';
      const availBal = e.currentAvailableBalance ?? 0;
      const trClass = lop > 0 ? 'has-lop' : (idx % 2 === 1 ? 'even-row' : '');

      return `
        <tr class="${trClass}">
          <td class="col-num">${idx + 1}</td>
          <td class="col-id">${e.employeeId || '—'}</td>
          <td class="col-name">${e.fullName || '—'}</td>
          <td class="col-dept">${dept}</td>
          <td class="col-stat">${leavesTaken} d</td>
          <td class="col-stat ${paid > 0 ? 'text-paid' : ''}">${paid} d</td>
          <td class="col-lop">${lopBadge}</td>
          <td class="col-pct">${deductPct}</td>
          <td class="col-amt">${estDeduct}</td>
          <td class="col-bal">${availBal} d</td>
        </tr>
      `;
    }).join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>Orakris_Payroll_Leave_Audit_${monthName}_${year}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 12mm 10mm 14mm 10mm;
    }
    *, *::before, *::after {
      box-sizing: border-box;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    html, body {
      margin: 0;
      padding: 0;
      background: #ffffff;
      color: #0f172a;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 10px;
      line-height: 1.35;
    }
    .print-doc {
      width: 100%;
      max-width: 100%;
      margin: 0 auto;
      padding: 4px;
    }

    /* Official Letterhead */
    .header-border {
      border-bottom: 2.5px solid #0f172a;
      padding-bottom: 10px;
      margin-bottom: 12px;
    }
    .header-top {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
    }
    .corp-title {
      font-size: 19px;
      font-weight: 900;
      letter-spacing: -0.02em;
      color: #0f172a;
      text-transform: uppercase;
      margin: 0;
    }
    .corp-sub {
      font-size: 9.5px;
      font-weight: 600;
      color: #0284c7;
      margin: 2px 0 0 0;
      letter-spacing: 0.02em;
    }
    .corp-dept {
      font-size: 8.5px;
      color: #64748b;
      margin: 2px 0 0 0;
    }
    .ref-block {
      text-align: right;
    }
    .ref-tag {
      display: inline-block;
      font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
      font-size: 9.5px;
      font-weight: 700;
      background: #f1f5f9;
      color: #1e293b;
      padding: 3px 8px;
      border-radius: 4px;
      border: 1px solid #cbd5e1;
    }
    .ref-meta {
      font-size: 8.5px;
      font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
      color: #64748b;
      margin-top: 4px;
    }

    /* Meta Bar */
    .meta-bar {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      margin-top: 10px;
      padding-top: 8px;
      border-top: 1px solid #e2e8f0;
    }
    .meta-box {
      background: #f8fafc;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 5px 8px;
    }
    .meta-lbl {
      font-size: 8px;
      font-weight: 700;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      display: block;
    }
    .meta-txt {
      font-size: 10.5px;
      font-weight: 700;
      color: #0f172a;
      margin-top: 1px;
      display: block;
    }

    /* KPI Summary Cards */
    .kpi-row {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      margin-bottom: 12px;
    }
    .kpi-cell {
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      padding: 7px 9px;
      background: #f8fafc;
    }
    .kpi-cell.lop-alert {
      border-color: #fecdd3;
      background: #fff1f2;
    }
    .kpi-lbl {
      font-size: 8px;
      font-weight: 700;
      text-transform: uppercase;
      color: #64748b;
      letter-spacing: 0.03em;
      margin: 0;
    }
    .kpi-lbl.lop-lbl {
      color: #be123c;
    }
    .kpi-num {
      font-size: 15px;
      font-weight: 800;
      color: #0f172a;
      margin: 2px 0 0 0;
      font-family: ui-monospace, Menlo, Monaco, Consolas, monospace;
    }
    .kpi-num.lop-num {
      color: #be123c;
    }

    /* Workforce Audit Table */
    table {
      width: 100%;
      border-collapse: collapse;
      font-size: 9px;
      margin-bottom: 16px;
    }
    thead {
      display: table-header-group;
    }
    th {
      background: #f1f5f9 !important;
      color: #1e293b;
      font-weight: 700;
      text-transform: uppercase;
      font-size: 8px;
      letter-spacing: 0.03em;
      padding: 5px 6px;
      border: 1px solid #cbd5e1;
      text-align: left;
      white-space: nowrap;
    }
    td {
      padding: 4px 6px;
      border: 1px solid #e2e8f0;
      color: #1e293b;
      vertical-align: middle;
    }
    tr {
      page-break-inside: avoid;
    }
    .even-row {
      background-color: #f8fafc;
    }
    .has-lop {
      background-color: #fff1f2 !important;
    }
    .col-num { width: 24px; text-align: center; color: #94a3b8; font-family: monospace; }
    .col-id { width: 85px; font-family: monospace; font-weight: 700; color: #0f172a; }
    .col-name { font-weight: 600; color: #0f172a; }
    .col-dept { color: #475569; font-size: 8.5px; }
    .col-stat { text-align: center; font-family: monospace; }
    .text-paid { color: #047857; font-weight: 600; }
    .col-lop { text-align: center; font-family: monospace; }
    .col-pct { text-align: center; font-family: monospace; font-weight: 600; }
    .col-amt { text-align: right; font-family: monospace; font-weight: 600; }
    .col-bal { text-align: center; font-family: monospace; font-weight: 600; }

    .lop-tag {
      display: inline-block;
      padding: 1px 4px;
      border-radius: 3px;
      background: #ffe4e6;
      color: #9f1239;
      font-weight: 800;
      font-size: 8.5px;
      border: 1px solid #fecdd3;
    }
    .pending-tag {
      font-size: 8px;
      color: #b45309;
      font-weight: 600;
    }
    .zero-muted {
      color: #94a3b8;
    }

    /* Sign-off & Audit Certification */
    .sign-section {
      page-break-inside: avoid;
      margin-top: 20px;
      padding-top: 12px;
      border-top: 1px solid #cbd5e1;
    }
    .cert-stmt {
      font-size: 8.5px;
      color: #475569;
      font-style: italic;
      margin-bottom: 20px;
      line-height: 1.4;
    }
    .sign-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 28px;
    }
    .sign-box {
      text-align: left;
    }
    .sign-line {
      border-bottom: 1px solid #475569;
      height: 32px;
      margin-bottom: 5px;
    }
    .sign-role {
      font-size: 9.5px;
      font-weight: 700;
      color: #0f172a;
      margin: 0;
    }
    .sign-desc {
      font-size: 8px;
      color: #64748b;
      margin: 1px 0 0 0;
    }

    /* Footer Note */
    .doc-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 14px;
      padding-top: 6px;
      border-top: 1px solid #e2e8f0;
      font-size: 7.5px;
      color: #94a3b8;
    }
  </style>
</head>
<body>
  <div class="print-doc">
    <!-- Header -->
    <div class="header-border">
      <div class="header-top">
        <div>
          <h1 class="corp-title">Orakris Med Solutions</h1>
        </div>
        <div class="ref-block">
          <div class="ref-tag">ORAK-AUDIT-${year}-${String(month).padStart(2, '0')}</div>
          <div class="ref-meta">Generated: ${genDateStr}, ${genTimeStr}</div>
        </div>
      </div>

      <div class="meta-bar">
        <div class="meta-box">
          <span class="meta-lbl">Audit Period</span>
          <span class="meta-txt">${monthName} ${year}</span>
        </div>
        <div class="meta-box">
          <span class="meta-lbl">Working Days</span>
          <span class="meta-txt">${workingDays} Days</span>
        </div>
        <div class="meta-box">
          <span class="meta-lbl">Calendar Days</span>
          <span class="meta-txt">${calendarDays} Days</span>
        </div>
        <div class="meta-box">
          <span class="meta-lbl">Report Scope</span>
          <span class="meta-txt">${emps.length} Staff Audited</span>
        </div>
      </div>
    </div>

    <!-- KPI Summary Ribbon -->
    <div class="kpi-row">
      <div class="kpi-cell">
        <p class="kpi-lbl">Total Monitored Workforce</p>
        <p class="kpi-num">${totalStaff}</p>
      </div>
      <div class="kpi-cell">
        <p class="kpi-lbl">Staff on Leave (${monthName})</p>
        <p class="kpi-num" style="color: #0284c7;">${staffOnLeave}</p>
      </div>
      <div class="kpi-cell">
        <p class="kpi-lbl">Total Leaves Taken</p>
        <p class="kpi-num">${totalLeaves} d</p>
      </div>
      <div class="kpi-cell ${totalLop > 0 ? 'lop-alert' : ''}">
        <p class="kpi-lbl ${totalLop > 0 ? 'lop-lbl' : ''}">Total Loss of Pay (LOP)</p>
        <p class="kpi-num ${totalLop > 0 ? 'lop-num' : ''}">${totalLop} d</p>
      </div>
    </div>

    <!-- Workforce Audit Table -->
    <table>
      <thead>
        <tr>
          <th style="width: 24px; text-align: center;">#</th>
          <th style="width: 85px;">Emp ID</th>
          <th>Employee Name</th>
          <th>Team / Dept</th>
          <th style="width: 65px; text-align: center;">Leaves Taken</th>
          <th style="width: 60px; text-align: center;">Paid</th>
          <th style="width: 60px; text-align: center;">LOP Days</th>
          <th style="width: 75px; text-align: center;">Salary Deduct %</th>
          <th style="width: 85px; text-align: right;">Est. Deduction</th>
          <th style="width: 65px; text-align: center;">Bal. Avail</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
    </table>

    <!-- Certification & Signatures -->
    <div class="sign-section">
      <p class="cert-stmt">
        "I hereby certify that the workforce leave utilization and Loss of Pay (LOP) deductions listed in this document have been reconciled with biometric attendance records, approved manager applications, and official Orakris leave policies for the audit period stated above."
      </p>
      <div class="sign-grid">
        <div class="sign-box">
          <div class="sign-line"></div>
          <p class="sign-role">HR Operations Lead</p>
          <p class="sign-desc">Verified & Reconciled</p>
        </div>
        <div class="sign-box">
          <div class="sign-line"></div>
          <p class="sign-role">Finance & Payroll Lead</p>
          <p class="sign-desc">Audited & Processed</p>
        </div>
        <div class="sign-box">
          <div class="sign-line"></div>
          <p class="sign-role">Authorized Signatory</p>
          <p class="sign-desc">Approved for Payroll Release</p>
        </div>
      </div>
    </div>

    <!-- Document Footer -->
    <div class="doc-footer">
      <span>Confidential &bull; Orakris Med Solutions Internal Payroll Audit &bull; Generated by Orakris ERP System</span>
      <span>Page 1 of Audit Document &bull; ${monthName} ${year}</span>
    </div>
  </div>
</body>
</html>`;
  };

  const handlePrintAuditDoc = () => {
    try {
      const existingFrame = document.getElementById('orakris-audit-print-frame');
      if (existingFrame) {
        existingFrame.remove();
      }

      const docHtml = generateAuditDocHtml({
        monthlyReport,
        employees: filteredPrintEmployees,
        month: selectedMonth,
        year: selectedYear,
        monthName: MONTH_NAMES[selectedMonth - 1] || 'Month',
      });

      const iframe = document.createElement('iframe');
      iframe.id = 'orakris-audit-print-frame';
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      document.body.appendChild(iframe);

      const frameDoc = iframe.contentWindow.document;
      frameDoc.open();
      frameDoc.write(docHtml);
      frameDoc.close();

      setTimeout(() => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      }, 350);
    } catch (err) {
      console.error('Print generation failed:', err);
      window.print();
    }
  };

  return (
    <div className="animate-fade-in space-y-6 pb-12">

      {/* ─────────────────────────────────────────────────────────────
          1. Header Card (Matching Orakris HR pages)
      ───────────────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">
            {isAdminMode ? 'Leave Administration ' : 'Leave Management'}
          </h1>
        </div>

        {/* Sleek Segmented Tabs (Pic 4) */}
        <div className="flex items-center gap-1 p-1 bg-slate-100/90 rounded-xl border border-slate-200/60 shadow-2xs overflow-x-auto max-w-full w-full lg:w-auto">
          <button
            type="button"
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer shrink-0 ${
              activeTab === 'summary'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200/60 font-semibold'
                : 'text-slate-500 hover:text-slate-900 font-medium'
            }`}
          >
            Monthly Summary
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('records')}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer shrink-0 ${
              activeTab === 'records'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200/60 font-semibold'
                : 'text-slate-500 hover:text-slate-900 font-medium'
            }`}
          >
            <span>Leave Records</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold transition-colors ${
              activeTab === 'records' ? 'bg-slate-100 text-slate-800' : 'bg-slate-200/70 text-slate-600'
            }`}>
              {allLeaves.length}
            </span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('balances')}
            className={`px-4 py-2 rounded-lg text-xs font-bold transition-all cursor-pointer shrink-0 ${
              activeTab === 'balances'
                ? 'bg-white text-slate-900 shadow-xs border border-slate-200/60 font-semibold'
                : 'text-slate-500 hover:text-slate-900 font-medium'
            }`}
          >
            Balances Directory
          </button>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          2. Month Selector & Combined Action Controls (Pic 3)
      ───────────────────────────────────────────────────────────── */}
      <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Month & Year Selectors */}
        <div className="flex items-center gap-2 w-full md:w-auto">
          <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700">
            <FiCalendar className="text-blue-600" size={14} />
            <select
              value={selectedMonth}
              onChange={(e) => handleMonthYearChange(parseInt(e.target.value, 10), selectedYear)}
              className="bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
            >
              {MONTH_NAMES.map((m, idx) => (
                <option key={m} value={idx + 1}>{m}</option>
              ))}
            </select>
            <span className="text-slate-300">/</span>
            <select
              value={selectedYear}
              onChange={(e) => handleMonthYearChange(selectedMonth, parseInt(e.target.value, 10))}
              className="bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
            >
              {[2024, 2025, 2026, 2027, 2028].map(yr => (
                <option key={yr} value={yr}>{yr}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Combined Action Buttons: [Generate Summary] + [Export ▾] Dropdown */}
        <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full md:w-auto justify-end">
          <button
            onClick={() => fetchMonthlySummary(selectedMonth, selectedYear, false)}
            disabled={loadingSummary}
            className="flex items-center justify-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs transition-colors shadow-xs cursor-pointer disabled:opacity-60"
          >
            <FiRefreshCw className={loadingSummary ? 'animate-spin' : ''} size={13} />
            <span className="truncate">Generate Summary</span>
          </button>

          {/* Export Dropdown Menu */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setExportMenuOpen(prev => !prev)}
              className="w-full sm:w-auto flex items-center justify-center gap-1.5 px-3.5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-xs transition-colors shadow-xs cursor-pointer"
            >
              <FiDownload size={13} className="text-slate-500" />
              <span>Export</span>
              <FiChevronDown size={12} className={`text-slate-400 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {exportMenuOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setExportMenuOpen(false)} />
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-40 text-xs animate-in fade-in zoom-in-95 duration-100">
                  <button
                    type="button"
                    onClick={() => { setExportMenuOpen(false); handleExportCsv(); }}
                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 font-semibold flex items-center gap-2 cursor-pointer transition-colors"
                  >
                    <FiDownload size={13} className="text-blue-600" />
                    <span>Download CSV Report</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setExportMenuOpen(false); setPrintDocOpen(true); }}
                    className="w-full text-left px-3.5 py-2 hover:bg-slate-50 text-slate-700 font-semibold flex items-center gap-2 cursor-pointer transition-colors"
                  >
                    <FiPrinter size={13} className="text-emerald-600" />
                    <span>Print Audit Document</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          3. Metric Cards (5 Cards matching media_1793202859938_feb2f0bc.png)
      ───────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3.5">
        <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Workforce</p>
          <p className="text-2xl font-black text-slate-800 mt-1">
            {monthlyReport?.totalEmployees ?? usersList.length ?? 0}
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-amber-200 shadow-sm bg-amber-50/20">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-700">Pending Requests</p>
          <p className="text-2xl font-black text-amber-800 mt-1">
            {monthlyReport?.totalPendingRequests ?? allLeaves.filter(l => l.status === 'PENDING').length ?? 0}
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-blue-200 shadow-sm bg-blue-50/20">
          <p className="text-xs font-bold uppercase tracking-wider text-blue-700">Approved Leaves</p>
          <p className="text-2xl font-black text-blue-800 mt-1">
            {allLeaves.filter(l => l.status === 'APPROVED').length}
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-emerald-200 shadow-sm bg-emerald-50/20">
          <p className="text-xs font-bold uppercase tracking-wider text-emerald-700">Paid Leave Days</p>
          <p className="text-2xl font-black text-emerald-800 mt-1">
            {monthlyReport?.totalPaidDays ?? 0}
          </p>
        </div>

        <div className="bg-white p-4 rounded-2xl border border-rose-200 shadow-sm bg-rose-50/20 col-span-2 sm:col-span-1">
          <p className="text-xs font-bold uppercase tracking-wider text-rose-700">LOP / Unpaid Days</p>
          <p className="text-2xl font-black text-rose-800 mt-1">
            {monthlyReport?.totalLopDays ?? 0}
          </p>
        </div>
      </div>

      {/* ─────────────────────────────────────────────────────────────
          TAB 1: Monthly Summary & Payroll Readiness
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'summary' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          {/* Filters Bar */}
          <div className="p-5 border-b border-slate-100 bg-slate-50/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="relative flex-1 w-full md:max-w-md">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search employee name, ID, or job title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              {/* Department */}
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-medium">
                <FiFilter className="text-slate-400" />
                <span className="text-slate-500 font-semibold">Dept:</span>
                <select
                  value={deptFilter}
                  onChange={(e) => setDeptFilter(e.target.value)}
                  className="bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
                >
                  {availableDepts.map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Status */}
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-medium">
                <span className="text-slate-500 font-semibold">Filter:</span>
                <select
                  value={summaryStatusFilter}
                  onChange={(e) => setSummaryStatusFilter(e.target.value)}
                  className="bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
                >
                  <option value="ALL">All Staff</option>
                  <option value="HAS_LOP">Has LOP Days</option>
                  <option value="ON_LEAVE">Took Leaves in Month</option>
                  <option value="ZERO_LEAVE">Zero Leaves Taken</option>
                  <option value="HAS_PENDING">Has Pending Requests</option>
                </select>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-3.5 px-4">Employee</th>
                  <th className="py-3.5 px-3">Department & Role</th>
                  <th className="py-3.5 px-3 text-center">Total Leaves</th>
                  <th className="py-3.5 px-3 text-center">Paid Days</th>
                  <th className="py-3.5 px-3 text-center">Unpaid Days</th>
                  <th className="py-3.5 px-3 text-center">LOP (Days)</th>
                  <th className="py-3.5 px-4 text-right">Est. Deduction</th>
                  <th className="py-3.5 px-3 text-center">Annual Bal.</th>
                  <th className="py-3.5 px-3 text-center">Pending</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loadingSummary ? (
                  <tr>
                    <td colSpan="10" className="py-12 text-center text-slate-400">
                      <FiRefreshCw className="animate-spin inline-block mr-2 text-blue-600" size={16} />
                      <span className="font-semibold text-xs">Generating monthly leave summary...</span>
                    </td>
                  </tr>
                ) : filteredSummaryEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="py-12 text-center text-slate-500">
                      <p className="font-bold text-sm">No employee leave records found</p>
                      <p className="text-xs text-slate-400 mt-1">Try adjusting the search or department filter</p>
                    </td>
                  </tr>
                ) : (
                  filteredSummaryEmployees.map(emp => {
                    const hasLop = (emp.lopDays ?? 0) > 0;
                    return (
                      <tr key={emp.employeeId} className={`hover:bg-slate-50/80 transition-colors ${hasLop ? 'bg-rose-50/20' : ''}`}>
                        {/* Employee */}
                        <td className="py-3 px-4">
                          <div className="font-bold text-slate-900">{emp.fullName}</div>
                          <span className="text-[10px] font-mono text-slate-500">{emp.employeeId}</span>
                        </td>

                        {/* Dept & Role */}
                        <td className="py-3 px-3">
                          <div className="text-slate-800 font-semibold">{emp.department || '—'}</div>
                          <div className="text-[11px] text-slate-400">{emp.jobTitle || 'Team Member'}</div>
                        </td>

                        {/* Total Leaves Taken */}
                        <td className="py-3 px-3 text-center font-bold text-slate-900 font-mono">
                          {emp.totalLeavesTaken ?? 0}
                        </td>

                        {/* Paid Days */}
                        <td className="py-3 px-3 text-center font-mono">
                          <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md font-semibold">
                            {emp.paidDays ?? 0}
                          </span>
                        </td>

                        {/* Unpaid Days */}
                        <td className="py-3 px-3 text-center font-mono text-slate-600">
                          {emp.unpaidDays ?? 0}
                        </td>

                        {/* LOP Days */}
                        <td className="py-3 px-3 text-center font-mono">
                          {hasLop ? (
                            <span className="text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-0.5 rounded-full font-bold">
                              {emp.lopDays} LOP
                            </span>
                          ) : (
                            <span className="text-slate-300">0</span>
                          )}
                        </td>

                        {/* Est. Deduction */}
                        <td className="py-3 px-4 text-right">
                          {hasLop && emp.lopDeductionAmount > 0 ? (
                            <div>
                              <span className="font-mono font-bold text-rose-700">
                                ₹{emp.lopDeductionAmount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          ) : hasLop ? (
                            <span className="text-[10px] text-amber-700 font-semibold bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
                              Pending Salary Config
                            </span>
                          ) : (
                            <span className="text-slate-300 font-mono">₹0.00</span>
                          )}
                        </td>

                        {/* Annual Balance */}
                        <td className="py-3 px-3 text-center font-mono font-semibold text-slate-700">
                          <span className="text-blue-600 font-bold">{emp.currentAvailableBalance ?? 0}</span>
                          <span className="text-slate-400 text-[10px]"> / {emp.annualAllocatedBalance ?? 24}</span>
                        </td>

                        {/* Pending */}
                        <td className="py-3 px-3 text-center">
                          {emp.pendingRequestsCount > 0 ? (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                              {emp.pendingRequestsCount} Pending
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => handleViewEmployeeProfile(emp)}
                              className="px-2.5 py-1 bg-white hover:bg-slate-50 border border-slate-200 text-blue-600 rounded-lg text-xs font-bold transition-colors cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                              title="Open individual employee summary page"
                            >
                              <FiExternalLink size={12} />
                              <span>Profile</span>
                            </button>
                            {isAdminMode && (
                              <button
                                onClick={() => handleOpenBalanceEdit(emp.employeeId, emp.fullName)}
                                className="px-2.5 py-1 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                                title="Edit leave balances"
                              >
                                <FiSliders size={12} />
                                <span>Balance</span>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Footer Count */}
          {monthlyReport && (
            <div className="p-4 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between text-xs text-slate-500 font-medium">
              <span>Showing {filteredSummaryEmployees.length} of {monthlyReport.totalEmployees} employees</span>
              <span className="font-semibold text-slate-700">Active Period: {MONTH_NAMES[selectedMonth - 1]} {selectedYear}</span>
            </div>
          )}
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 2: All Leave Records & Corrections
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'records' && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          {/* Filters Bar */}
          <div className="p-5 border-b border-slate-100 bg-slate-50/60 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="relative flex-1 w-full md:max-w-md">
              <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
              <input
                type="text"
                placeholder="Search leave applications..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
              <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-medium">
                <FiFilter className="text-slate-400" />
                <span className="text-slate-500 font-semibold">Status:</span>
                <select
                  value={recordStatusFilter}
                  onChange={(e) => setRecordStatusFilter(e.target.value)}
                  className="bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
                >
                  {STATUS_OPTIONS.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-1.5 text-xs font-medium">
                <span className="text-slate-500 font-semibold">Type:</span>
                <select
                  value={recordTypeFilter}
                  onChange={(e) => setRecordTypeFilter(e.target.value)}
                  className="bg-transparent font-bold text-slate-800 outline-none cursor-pointer"
                >
                  {LEAVE_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <button
                onClick={fetchAllLeaves}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-xs transition-colors shadow-xs cursor-pointer"
              >
                <FiRefreshCw className={loadingLeaves ? 'animate-spin text-blue-600' : ''} size={12} />
                <span>Refresh</span>
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase tracking-wider">
                  <th className="py-3.5 px-4">Employee</th>
                  <th className="py-3.5 px-3">Type</th>
                  <th className="py-3.5 px-3">Dates</th>
                  <th className="py-3.5 px-3 text-center">Days</th>
                  <th className="py-3.5 px-3 text-center">Paid / Unpaid</th>
                  <th className="py-3.5 px-3">Status</th>
                  <th className="py-3.5 px-4">Reason & Remarks</th>
                  {isAdminMode && <th className="py-3.5 px-4 text-right">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loadingLeaves ? (
                  <tr>
                    <td colSpan={isAdminMode ? 8 : 7} className="py-12 text-center text-slate-400">
                      <FiRefreshCw className="animate-spin inline-block mr-2 text-blue-600" size={16} />
                      <span className="font-semibold text-xs">Loading leave records...</span>
                    </td>
                  </tr>
                ) : filteredLeaves.length === 0 ? (
                  <tr>
                    <td colSpan={isAdminMode ? 8 : 7} className="py-12 text-center text-slate-500">
                      <p className="font-bold text-sm">No leave records match the filters</p>
                    </td>
                  </tr>
                ) : (
                  filteredLeaves.map(lv => {
                    const isPending = lv.status === 'PENDING';
                    const isRejected = lv.status === 'REJECTED';
                    const isApproved = lv.status === 'APPROVED';

                    return (
                      <tr key={lv.id} className="hover:bg-slate-50/80 transition-colors">
                        {/* Employee */}
                        <td className="py-3 px-4">
                          <div className="font-bold text-slate-900">{lv.employeeName || lv.employeeUsername || `User ${lv.employeeId}`}</div>
                          <span className="text-[10px] font-mono text-slate-500">{lv.employeeId}</span>
                        </td>

                        {/* Type */}
                        <td className="py-3 px-3">
                          <span className="font-semibold text-slate-800">{lv.leaveType}</span>
                          {lv.isHalfDay && (
                            <span className="block text-[10px] text-amber-600 font-semibold">
                              Half Day ({lv.halfDaySession})
                            </span>
                          )}
                        </td>

                        {/* Dates */}
                        <td className="py-3 px-3 font-mono text-slate-700 whitespace-nowrap">
                          {fmtDate(lv.startDate)} &rarr; {fmtDate(lv.endDate)}
                        </td>

                        {/* Days */}
                        <td className="py-3 px-3 text-center font-mono font-bold text-slate-900">
                          {lv.daysCount}
                        </td>

                        {/* Paid / Unpaid */}
                        <td className="py-3 px-3 text-center font-mono text-[11px]">
                          <span className="text-emerald-700 font-bold">{lv.paidDays ?? 0}P</span>
                          <span className="text-slate-300 mx-1">/</span>
                          <span className={`font-bold ${(lv.unpaidDays ?? 0) > 0 ? 'text-rose-700' : 'text-slate-400'}`}>
                            {lv.unpaidDays ?? 0}U
                          </span>
                        </td>

                        {/* Status */}
                        <td className="py-3 px-3">
                          {isPending && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200">
                              <FiClock size={12} /> Pending
                            </span>
                          )}
                          {isApproved && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <FiCheck size={12} /> Approved
                            </span>
                          )}
                          {isRejected && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-700 border border-rose-200">
                              <FiX size={12} /> Rejected
                            </span>
                          )}
                          {lv.status === 'CANCELLED' && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                              Cancelled
                            </span>
                          )}
                        </td>

                        {/* Reason & Remarks */}
                        <td className="py-3 px-4 max-w-xs">
                          <p className="text-slate-700 truncate font-medium" title={lv.reason}>{lv.reason || '—'}</p>
                          {lv.remarks && (
                            <p className="text-[10px] text-slate-400 italic truncate" title={lv.remarks}>{lv.remarks}</p>
                          )}
                        </td>

                        {/* Actions (Admin Only) */}
                        {isAdminMode && (
                          <td className="py-3 px-4 text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {isPending && (
                                <>
                                  <button
                                    onClick={() => handleOpenApproval(lv, 'APPROVED')}
                                    className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                                  >
                                    <FiCheck size={12} />
                                    <span>Approve</span>
                                  </button>
                                  <button
                                    onClick={() => handleOpenApproval(lv, 'REJECTED')}
                                    className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                                  >
                                    <FiX size={12} />
                                    <span>Reject</span>
                                  </button>
                                  <button
                                    onClick={() => handleRemindManager(lv)}
                                    disabled={remindingId === lv.id}
                                    className="p-1.5 bg-white border border-slate-200 hover:bg-slate-50 text-amber-700 rounded-lg text-xs transition-colors cursor-pointer shadow-2xs"
                                    title="Remind Manager"
                                  >
                                    <FiBell size={13} className={remindingId === lv.id ? 'animate-spin' : ''} />
                                  </button>
                                </>
                              )}

                              {isRejected && (
                                <button
                                  onClick={() => handleOpenReverse(lv)}
                                  className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-blue-600 rounded-lg text-xs font-bold transition-colors cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                                  title="Reverse rejection & clear LOP penalty"
                                >
                                  <FiRotateCcw size={12} />
                                  <span>Clear LOP</span>
                                </button>
                              )}

                              <button
                                onClick={() => handleOpenEditLeave(lv)}
                                className="px-2.5 py-1 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-lg text-xs font-semibold transition-colors cursor-pointer inline-flex items-center gap-1 shadow-2xs"
                                title="Edit & correct leave record"
                              >
                                <FiEdit2 size={12} />
                                <span>Edit</span>
                              </button>

                              <button
                                onClick={() => setDeleteConfirm({ open: true, leave: lv, saving: false })}
                                className="p-1.5 bg-white border border-slate-200 hover:bg-rose-50 text-slate-400 hover:text-rose-600 rounded-lg text-xs transition-colors cursor-pointer shadow-2xs"
                                title="Delete record permanently"
                              >
                                <FiTrash2 size={13} />
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          TAB 3: Balances & Entitlements Directory
      ───────────────────────────────────────────────────────────── */}
      {activeTab === 'balances' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Employee Directory List */}
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h3 className="text-sm font-bold text-slate-900">Employee Directory</h3>
              <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                {usersList.length} staff
              </span>
            </div>

            <div className="relative">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
              <input
                type="text"
                placeholder="Search employee..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>

            <div className="max-h-[500px] overflow-y-auto space-y-1 pr-1">
              {loadingUsers ? (
                <div className="py-8 text-center text-slate-400 text-xs">
                  <FiRefreshCw className="animate-spin inline-block mr-1 text-blue-600" size={14} /> Loading staff...
                </div>
              ) : (
                usersList
                  .filter(u => !searchQuery || (u.firstName + ' ' + u.lastName + ' ' + u.employeeId).toLowerCase().includes(searchQuery.toLowerCase()))
                  .map(u => {
                    const empId = u.employeeId || `EMP-${u.id}`;
                    const isSelected = selectedBalEmpId === empId;
                    const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.username;

                    return (
                      <div
                        key={u.id}
                        onClick={() => setSelectedBalEmpId(empId)}
                        className={`p-3 rounded-xl border transition-all cursor-pointer flex items-center justify-between gap-3 ${
                          isSelected
                            ? 'bg-blue-50/80 border-blue-200 text-blue-900 shadow-xs'
                            : 'bg-white border-slate-200/70 hover:bg-slate-50 text-slate-800'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="font-bold text-xs truncate">{name}</p>
                          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400 font-mono">
                            <span>{empId}</span>
                            {u.department && <span>&bull; {u.department}</span>}
                          </div>
                        </div>
                        <FiChevronRight size={14} className={isSelected ? 'text-blue-600' : 'text-slate-300'} />
                      </div>
                    );
                  })
              )}
            </div>
          </div>

          {/* Right: Balances Detail */}
          <div className="lg:col-span-2 space-y-4">
            {selectedBalEmpId ? (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
                  <div>
                    <h2 className="text-xl font-black text-slate-900 flex items-center gap-2.5">
                      <span>{selectedBalEmpId}</span>
                      <span className="text-xs px-2.5 py-0.5 rounded-full font-bold bg-blue-50 text-blue-700 border border-blue-200">
                        FY {selectedYear}
                      </span>
                    </h2>
                  </div>

                  <div className="flex items-center gap-2">
                    {isAdminMode && (
                      <button
                        onClick={() => handleOpenBalanceEdit(selectedBalEmpId, selectedBalEmpId)}
                        className="px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition-colors cursor-pointer inline-flex items-center gap-1.5 shadow-sm"
                      >
                        <FiEdit2 size={13} />
                        <span>Edit Balance</span>
                      </button>
                    )}
                    <button
                      onClick={() => handleViewEmployeeProfile({ employeeId: selectedBalEmpId, fullName: selectedBalEmpId })}
                      className="px-3.5 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors cursor-pointer inline-flex items-center gap-1.5 shadow-2xs"
                    >
                      <FiExternalLink size={13} />
                      <span>Full Leave Profile</span>
                    </button>
                  </div>
                </div>

                {loadingBalDetails && (
                  <div className="text-center text-blue-600 text-xs flex items-center justify-center gap-1.5 py-1">
                    <FiRefreshCw className="animate-spin" size={13} /> Fetching balance records...
                  </div>
                )}

                {/* 4 Stat Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Base Entitlement</p>
                    <p className="text-2xl font-black text-slate-800 mt-1">{empMonthlyBal?.annualLimit ?? 24}</p>
                  </div>
                  <div className="bg-emerald-50/40 border border-emerald-200 rounded-xl p-3.5">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Available</p>
                    <p className="text-2xl font-black text-emerald-800 mt-1">{empMonthlyBal?.totalPaidLeavesAvailableYear ?? 0}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Carried Forward</p>
                    <p className="text-2xl font-black text-slate-800 mt-1">{empMonthlyBal?.carriedForward ?? 0}</p>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Earned Leaves</p>
                    <p className="text-2xl font-black text-slate-800 mt-1">{empMonthlyBal?.earnedLeave ?? 0}</p>
                  </div>
                </div>

                {/* Balance History Table */}
                <div className="space-y-2 pt-2">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Balance History By Year</h4>
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider border-b border-slate-200">
                          <th className="py-2.5 px-3">Year</th>
                          <th className="py-2.5 px-3 text-center">Allocated</th>
                          <th className="py-2.5 px-3 text-center">Used (Paid)</th>
                          <th className="py-2.5 px-3 text-center">Reserved</th>
                          <th className="py-2.5 px-3 text-center">Available</th>
                          <th className="py-2.5 px-3 text-center">Carried Fwd</th>
                          <th className="py-2.5 px-3 text-center">Earned</th>
                          {isAdminMode && <th className="py-2.5 px-3 text-right">Action</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 font-mono">
                        {empBalances.length === 0 ? (
                          <tr>
                            <td colSpan={isAdminMode ? 8 : 7} className="py-6 text-center text-slate-400 font-sans text-xs">
                              Standard policy active (no override row).
                            </td>
                          </tr>
                        ) : (
                          empBalances.map(b => (
                            <tr key={b.id || b.year} className="hover:bg-slate-50">
                              <td className="py-2.5 px-3 font-bold text-slate-900">{b.year}</td>
                              <td className="py-2.5 px-3 text-center">{b.allocated}</td>
                              <td className="py-2.5 px-3 text-center text-slate-700">{b.used}</td>
                              <td className="py-2.5 px-3 text-center text-amber-600">{b.reserved}</td>
                              <td className="py-2.5 px-3 text-center font-bold text-emerald-700">
                                {Math.max(0, (b.allocated || 0) - (b.used || 0) - (b.reserved || 0))}
                              </td>
                              <td className="py-2.5 px-3 text-center">{b.carriedForward ?? 0}</td>
                              <td className="py-2.5 px-3 text-center">{b.earnedLeave ?? 0}</td>
                              {isAdminMode && (
                                <td className="py-2.5 px-3 text-right">
                                  <button
                                    onClick={() => handleOpenBalanceEdit(selectedBalEmpId, selectedBalEmpId, b)}
                                    className="text-blue-600 hover:text-blue-800 font-sans text-xs font-bold cursor-pointer"
                                  >
                                    Edit
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center text-slate-400">
                <FiDatabase size={24} className="mx-auto mb-2 opacity-60" />
                <p className="font-semibold text-sm">Select an employee from the directory to manage balances</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: Leave Correction
      ───────────────────────────────────────────────────────────── */}
      {editLeaveModal.open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">Leave Record Correction</h3>
                <p className="text-xs text-slate-500">
                  {editLeaveModal.leave?.employeeName || editLeaveModal.leave?.employeeId} &bull; ID: #{editLeaveModal.leave?.id}
                </p>
              </div>
              <button
                onClick={() => setEditLeaveModal(p => ({ ...p, open: false }))}
                className="text-slate-400 hover:text-slate-600 p-1 cursor-pointer"
              >
                <FiX size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Status</label>
                <select
                  value={editLeaveModal.formData.status}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: { ...p.formData, status: e.target.value }
                  }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                >
                  <option value="PENDING">PENDING</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="REJECTED">REJECTED</option>
                  <option value="CANCELLED">CANCELLED</option>
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Leave Type</label>
                <select
                  value={editLeaveModal.formData.leaveType}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: { ...p.formData, leaveType: e.target.value }
                  }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                >
                  {LEAVE_TYPES.filter(t => t !== 'ALL').map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={editLeaveModal.formData.startDate}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: { ...p.formData, startDate: e.target.value }
                  }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={editLeaveModal.formData.endDate}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: { ...p.formData, endDate: e.target.value }
                  }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Total Days</label>
                <input
                  type="number"
                  step="0.5"
                  value={editLeaveModal.formData.daysCount}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: { ...p.formData, daysCount: parseFloat(e.target.value) || 0 }
                  }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Paid Days</label>
                <input
                  type="number"
                  step="0.5"
                  value={editLeaveModal.formData.paidDays}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: {
                      ...p.formData,
                      paidDays: parseFloat(e.target.value) || 0,
                      unpaidDays: Math.max(0, (p.formData.daysCount || 0) - (parseFloat(e.target.value) || 0))
                    }
                  }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono text-emerald-800"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Unpaid (LOP) Days</label>
                <input
                  type="number"
                  step="0.5"
                  value={editLeaveModal.formData.unpaidDays}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: { ...p.formData, unpaidDays: parseFloat(e.target.value) || 0 }
                  }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono text-rose-800"
                />
              </div>

              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  id="penaltyCheck"
                  checked={editLeaveModal.formData.penaltyApplied}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: { ...p.formData, penaltyApplied: e.target.checked }
                  }))}
                  className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
                />
                <label htmlFor="penaltyCheck" className="text-xs font-semibold text-slate-700 cursor-pointer">
                  LOP Penalty Active
                </label>
              </div>

              <div className="col-span-2">
                <label className="block font-semibold text-slate-700 mb-1">HR Remarks</label>
                <textarea
                  rows={2}
                  value={editLeaveModal.formData.remarks}
                  onChange={(e) => setEditLeaveModal(p => ({
                    ...p,
                    formData: { ...p.formData, remarks: e.target.value }
                  }))}
                  placeholder="Reason for correction..."
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setEditLeaveModal(p => ({ ...p, open: false }))}
                className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEditLeave}
                disabled={editLeaveModal.saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold shadow-xs disabled:opacity-60 cursor-pointer"
              >
                {editLeaveModal.saving ? 'Saving...' : 'Save Correction'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: Reverse Rejection & Clear LOP
      ───────────────────────────────────────────────────────────── */}
      {reverseModal.open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-200 space-y-4">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <FiRotateCcw className="text-blue-600" />
              <span>Reverse Rejection & Clear LOP</span>
            </h3>
            <p className="text-xs text-slate-600">
              Clears the Loss of Pay (LOP) penalty for <strong>{reverseModal.leave?.employeeName || reverseModal.leave?.employeeId}</strong> (#{reverseModal.leave?.id}) and restores status to <strong>PENDING</strong>.
            </p>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Remarks</label>
              <input
                type="text"
                value={reverseModal.remarks}
                onChange={(e) => setReverseModal(p => ({ ...p, remarks: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setReverseModal({ open: false, leave: null, remarks: '', saving: false })}
                className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-700 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmReverse}
                disabled={reverseModal.saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold disabled:opacity-60 cursor-pointer"
              >
                {reverseModal.saving ? 'Processing...' : 'Confirm Reversal'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: Balance Upsert
      ───────────────────────────────────────────────────────────── */}
      {balanceModal.open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-200 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">Adjust Leave Balance</h3>
                <p className="text-xs text-slate-500">{balanceModal.employeeName} ({balanceModal.employeeId})</p>
              </div>
              <button onClick={() => setBalanceModal(p => ({ ...p, open: false }))} className="text-slate-400 cursor-pointer">
                <FiX size={18} />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Financial Year</label>
                <input
                  type="number"
                  value={balanceModal.year}
                  onChange={(e) => setBalanceModal(p => ({ ...p, year: parseInt(e.target.value, 10) }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Allocated (Entitlement)</label>
                <input
                  type="number"
                  step="0.5"
                  value={balanceModal.allocated}
                  onChange={(e) => setBalanceModal(p => ({ ...p, allocated: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Used (Paid Consumed)</label>
                <input
                  type="number"
                  step="0.5"
                  value={balanceModal.used}
                  onChange={(e) => setBalanceModal(p => ({ ...p, used: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Reserved (Pending)</label>
                <input
                  type="number"
                  step="0.5"
                  value={balanceModal.reserved}
                  onChange={(e) => setBalanceModal(p => ({ ...p, reserved: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Carried Forward</label>
                <input
                  type="number"
                  step="0.5"
                  value={balanceModal.carriedForward}
                  onChange={(e) => setBalanceModal(p => ({ ...p, carriedForward: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Earned Leaves</label>
                <input
                  type="number"
                  step="0.5"
                  value={balanceModal.earnedLeave}
                  onChange={(e) => setBalanceModal(p => ({ ...p, earnedLeave: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-mono"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setBalanceModal(p => ({ ...p, open: false }))}
                className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-700 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveBalance}
                disabled={balanceModal.saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold disabled:opacity-60 cursor-pointer"
              >
                {balanceModal.saving ? 'Saving...' : 'Update Balance'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ─────────────────────────────────────────────────────────────
          MODAL: Approve / Reject Leave
      ───────────────────────────────────────────────────────────── */}
      {actionModal.open && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl border border-slate-200 space-y-4">
            <h3 className="text-base font-bold text-slate-900">
              {actionModal.status === 'APPROVED' ? 'Approve Leave Request' : 'Reject Leave Request'}
            </h3>
            <p className="text-xs text-slate-600">
              {actionModal.leave?.employeeName || actionModal.leave?.employeeId} &bull; {fmtDate(actionModal.leave?.startDate)} to {fmtDate(actionModal.leave?.endDate)} ({actionModal.leave?.daysCount} days)
            </p>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">Remarks</label>
              <input
                type="text"
                value={actionModal.remarks}
                onChange={(e) => setActionModal(p => ({ ...p, remarks: e.target.value }))}
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setActionModal({ open: false, leave: null, status: 'APPROVED', remarks: '', saving: false })}
                className="px-4 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-700 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmApproval}
                disabled={actionModal.saving}
                className={`px-4 py-2 text-white rounded-xl text-xs font-bold disabled:opacity-60 cursor-pointer ${
                  actionModal.status === 'APPROVED' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'
                }`}
              >
                {actionModal.saving ? 'Processing...' : `Confirm ${actionModal.status}`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={deleteConfirm.open}
        title="Delete Leave Application"
        message={`Are you sure you want to permanently delete this leave application for ${deleteConfirm.leave?.employeeName || deleteConfirm.leave?.employeeId}? This action cannot be undone.`}
        confirmText={deleteConfirm.saving ? 'Deleting...' : 'Delete Permanently'}
        onConfirm={handleDeleteLeave}
        onClose={() => setDeleteConfirm({ open: false, leave: null, saving: false })}
        variant="danger"
        loading={deleteConfirm.saving}
      />

      {/* ─────────────────────────────────────────────────────────────
          MODAL: Printable & Exportable Payroll Leave Audit Document
      ───────────────────────────────────────────────────────────── */}
      {printDocOpen && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4 md:p-6 bg-slate-900/70 backdrop-blur-xs overflow-hidden">
          {/* Backdrop click to dismiss */}
          <div className="fixed inset-0" onClick={() => setPrintDocOpen(false)} />

          {/* In-page Print CSS Isolator for direct Ctrl+P */}
          <style>{`
            @media print {
              nav, aside, header, button, input, [class*="AutoFill"], [id*="autofill"], .print\\:hidden {
                display: none !important;
              }
              .fixed.inset-0 {
                position: static !important;
                background: transparent !important;
                backdrop-filter: none !important;
                overflow: visible !important;
                padding: 0 !important;
              }
              .max-h-\\[92vh\\] {
                max-height: none !important;
              }
              .overflow-y-auto {
                overflow: visible !important;
              }
              .shadow-2xl, .shadow-xl, .shadow-sm {
                box-shadow: none !important;
              }
              .border {
                border: none !important;
              }
              #payroll-leave-report {
                max-width: 100% !important;
                box-shadow: none !important;
                border: none !important;
                padding: 0 !important;
              }
              @page {
                size: A4 portrait;
                margin: 12mm 10mm 15mm 10mm;
              }
              table {
                width: 100% !important;
                page-break-inside: auto !important;
              }
              thead {
                display: table-header-group !important;
              }
              tr {
                page-break-inside: avoid !important;
              }
            }
          `}</style>

          {/* Dialog Container with internal scroll */}
          <div className="relative bg-white rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            
            {/* 1. STICKY MODAL HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-4 border-b border-slate-200 bg-white shrink-0 z-20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600 shrink-0">
                  <FiFileText size={20} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <span>Monthly Payroll Leave Audit Document</span>
                    <span className="text-[11px] px-2.5 py-0.5 rounded-full font-bold bg-blue-50 text-blue-700 border border-blue-200">
                      {MONTH_NAMES[selectedMonth - 1]} {selectedYear}
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 font-medium">
                    {monthlyReport?.employees?.length ?? 0} total workforce &bull; Official Payroll & LOP Audit Report
                  </p>
                </div>
              </div>

              {/* Action Buttons: [Download CSV] [Print / Export PDF] [X Close] */}
              <div className="flex items-center gap-2 self-end sm:self-auto">
                <button
                  type="button"
                  onClick={handleExportCsv}
                  className="px-3.5 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 font-bold rounded-xl text-xs transition-colors shadow-2xs cursor-pointer inline-flex items-center gap-1.5"
                  title="Download CSV Report"
                >
                  <FiDownload size={14} className="text-blue-600" />
                  <span>Download CSV</span>
                </button>

                <button
                  type="button"
                  onClick={handlePrintAuditDoc}
                  className="px-4 py-2 bg-slate-900 hover:bg-black text-white font-bold rounded-xl text-xs transition-colors shadow-xs cursor-pointer inline-flex items-center gap-2"
                  title="Print or Export as Clean Multi-Page A4 PDF"
                >
                  <FiPrinter size={14} />
                  <span>Print / Export PDF</span>
                </button>

                <button
                  type="button"
                  onClick={() => setPrintDocOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-slate-800 hover:bg-slate-100 transition-colors ml-1 cursor-pointer"
                  title="Close (Esc)"
                  aria-label="Close modal"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* In-Modal Filter Bar (Quick search & LOP toggle) */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-6 py-2.5 bg-slate-50/90 border-b border-slate-200 text-xs shrink-0 print:hidden">
              <div className="relative flex-1 max-w-sm">
                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                <input
                  type="text"
                  placeholder="Filter by name, ID, or team..."
                  value={printSearch}
                  onChange={(e) => setPrintSearch(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPrintLopOnly(prev => !prev)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    printLopOnly
                      ? 'bg-rose-100 text-rose-800 border border-rose-300 shadow-2xs'
                      : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {printLopOnly ? '✓ LOP Employees Only' : 'Filter: LOP Only'}
                </button>
                <span className="text-[11px] text-slate-400 font-medium">
                  Showing {filteredPrintEmployees.length} of {monthlyReport?.employees?.length ?? 0}
                </span>
              </div>
            </div>

            {/* 2. SCROLLABLE DOCUMENT BODY */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-8 bg-slate-100/60 print:p-0 print:bg-white print:overflow-visible">
              <div
                id="payroll-leave-report"
                className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-8 space-y-6 max-w-4xl mx-auto print:max-w-none print:shadow-none print:border-none print:p-0"
              >
                {/* Official Letterhead */}
                <div className="border-b-2 border-slate-900 pb-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h2 className="text-2xl font-black tracking-tight text-slate-900 uppercase">
                        Orakris Med Solutions
                      </h2>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] font-mono px-2 py-1 rounded bg-slate-100 text-slate-700 border border-slate-200 uppercase font-bold">
                        ORAK-AUDIT-{selectedYear}-{String(selectedMonth).padStart(2, '0')}
                      </span>
                      <p className="text-xs font-mono text-slate-500 mt-1">
                        Generated: {fmtDateTime(new Date())}
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-200 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Audit Period</span>
                      <span className="font-bold text-blue-700 text-sm">{MONTH_NAMES[selectedMonth - 1]} {selectedYear}</span>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Working Days</span>
                      <span className="font-bold font-mono text-slate-800 text-sm">{monthlyReport?.workingDays ?? 22} Days</span>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Calendar Days</span>
                      <span className="font-bold font-mono text-slate-800 text-sm">{monthlyReport?.calendarDays ?? 30} Days</span>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2">
                      <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block">Audited Staff</span>
                      <span className="font-bold font-mono text-slate-800 text-sm">{filteredPrintEmployees.length} Staff</span>
                    </div>
                  </div>
                </div>

                {/* KPI Metrics Summary Bar */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs font-mono">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-sans">Total Staff</span>
                    <p className="text-xl font-bold text-slate-900 mt-0.5">{monthlyReport?.totalEmployees ?? 0}</p>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider font-sans">Staff on Leave</span>
                    <p className="text-xl font-bold text-blue-700 mt-0.5">{monthlyReport?.employeesOnLeave ?? 0}</p>
                  </div>
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider font-sans">Total Leaves Taken</span>
                    <p className="text-xl font-bold text-slate-900 mt-0.5">{monthlyReport?.totalLeaveDaysTaken ?? 0} d</p>
                  </div>
                  <div className={monthlyReport?.totalLopDays > 0 ? 'bg-rose-50 border border-rose-200 rounded-lg p-2 -m-2' : ''}>
                    <span className="text-[10px] font-bold text-rose-700 uppercase tracking-wider font-sans">Total Loss of Pay</span>
                    <p className="text-xl font-bold text-rose-700 mt-0.5">{monthlyReport?.totalLopDays ?? 0} d</p>
                  </div>
                </div>

                {/* Detailed Employee Leave & LOP Audit Table */}
                <div className="border border-slate-200 rounded-xl overflow-hidden shadow-2xs">
                  <table className="w-full text-left text-xs">
                    <thead className="sticky top-0 bg-slate-100 z-10">
                      <tr className="bg-slate-100 text-[10px] font-bold text-slate-700 uppercase tracking-wider border-b border-slate-200">
                        <th className="py-2.5 px-2 text-center w-8">#</th>
                        <th className="py-2.5 px-3">Emp ID</th>
                        <th className="py-2.5 px-3">Employee Name</th>
                        <th className="py-2.5 px-3">Team / Dept</th>
                        <th className="py-2.5 px-2 text-center">Leaves Taken</th>
                        <th className="py-2.5 px-2 text-center">Paid</th>
                        <th className="py-2.5 px-2 text-center">LOP Days</th>
                        <th className="py-2.5 px-2 text-center">Salary Deduct %</th>
                        <th className="py-2.5 px-3 text-right">Est. Deduction</th>
                        <th className="py-2.5 px-2 text-center">Bal. Avail</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 font-mono text-[11px]">
                      {filteredPrintEmployees.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="py-8 text-center text-slate-400 font-sans text-xs">
                            No employees match the audit filter.
                          </td>
                        </tr>
                      ) : (
                        filteredPrintEmployees.map((e, idx) => {
                          const lop = e.lopDays || 0;
                          const deductPct = ((lop / 30) * 100).toFixed(1);
                          return (
                            <tr key={e.employeeId || idx} className={lop > 0 ? 'bg-rose-50/50 font-medium' : 'hover:bg-slate-50'}>
                              <td className="py-2 px-2 text-center text-slate-400 font-sans text-[10px]">{idx + 1}</td>
                              <td className="py-2 px-3 font-bold text-slate-800">{e.employeeId}</td>
                              <td className="py-2 px-3 font-sans font-semibold text-slate-900">{e.fullName}</td>
                              <td className="py-2 px-3 font-sans text-slate-600">{e.department && e.department !== '—' ? e.department : 'General'}</td>
                              <td className="py-2 px-2 text-center font-bold">{e.totalLeavesTaken ?? 0} d</td>
                              <td className="py-2 px-2 text-center text-emerald-700 font-semibold">{e.paidDays ?? 0} d</td>
                              <td className="py-2 px-2 text-center">
                                {lop > 0 ? (
                                  <span className="px-1.5 py-0.5 rounded font-bold text-[10px] bg-rose-100 text-rose-800 border border-rose-200">
                                    {lop} d
                                  </span>
                                ) : (
                                  <span className="text-slate-400">0.0 d</span>
                                )}
                              </td>
                              <td className="py-2 px-2 text-center">
                                {lop > 0 ? (
                                  <span className="font-bold text-rose-700">{deductPct}%</span>
                                ) : (
                                  <span className="text-slate-400">0.0%</span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right">
                                {lop > 0 && e.lopDeductionAmount > 0 ? (
                                  <span className="font-bold text-slate-900">
                                    ₹{Number(e.lopDeductionAmount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                  </span>
                                ) : lop > 0 ? (
                                  <span className="text-[10px] text-amber-700 font-sans font-semibold">Pending Setup</span>
                                ) : (
                                  <span className="text-slate-400">—</span>
                                )}
                              </td>
                              <td className="py-2 px-2 text-center text-slate-700 font-bold">{e.currentAvailableBalance ?? 0} d</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Signatures & Approvals */}
                <div className="pt-8 border-t border-slate-200 mt-8 space-y-6">
                  <p className="text-[11px] text-slate-500 italic">
                    "I hereby certify that the workforce leave utilization and Loss of Pay (LOP) deductions listed in this document have been reconciled with biometric attendance records, approved manager applications, and official Orakris leave policies for the audit period stated above."
                  </p>
                  <div className="grid grid-cols-3 gap-8 text-xs">
                    <div>
                      <div className="border-b border-slate-400 h-10 mb-2"></div>
                      <p className="font-bold text-slate-800">HR Operations Lead</p>
                      <p className="text-[10px] text-slate-400">Verified & Reconciled</p>
                    </div>
                    <div>
                      <div className="border-b border-slate-400 h-10 mb-2"></div>
                      <p className="font-bold text-slate-800">Finance & Payroll Lead</p>
                      <p className="text-[10px] text-slate-400">Audited & Processed</p>
                    </div>
                    <div>
                      <div className="border-b border-slate-400 h-10 mb-2"></div>
                      <p className="font-bold text-slate-800">Authorized Signatory</p>
                      <p className="text-[10px] text-slate-400">Approved for Payroll Release</p>
                    </div>
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>,
        document.body
      )}

    </div>
  );
}
